// src/lib/packageRecommendation.ts
// Dynamische Paketempfehlungs-Engine auf Basis echter Audit-Signale

export type PackageKey = "diagnose" | "optimierung" | "transformation" | "retainer";

export type PackageRecommendation = {
  key: PackageKey;
  name: string;
  shortLabel: string;
  priceLabel: string;
  durationLabel: string;
  ctaLabel: string;
  urgency: "niedrig" | "mittel" | "hoch";
  headline: string;
  rationale: string;
  outcome: string[];
  reasons: string[];
  nextStep: string;
  monthlyLoss: number;
  annualLoss: number;
  scoreSignals: {
    overallScore: number;
    blockingStages: number;
    monthlyLoss: number;
    annualLoss: number;
    closingRate?: number;
    maturityGap?: number;
  };
};

type AuditResultLike = Record<string, unknown> | null | undefined;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export const formatCurrency = (value: number) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0);

export const getSafeNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export function extractCommercialSignals(result: AuditResultLike) {
  const r = result as Record<string, unknown> | null | undefined;
  const metrics = (r?.computedMetrics ?? {}) as Record<string, unknown>;
  const goalPath = (metrics?.goalPath ?? {}) as Record<string, unknown>;
  const funnelDiagnosis = (metrics?.funnelDiagnosis ?? {}) as Record<string, unknown>;
  const costInefficiency = (metrics?.costInefficiency ?? {}) as Record<string, unknown>;
  const performanceSummary = (metrics?.performanceSummary ?? {}) as Record<string, unknown>;

  // overallScore: verschiedene Quellen, defensiv
  const overallScore =
    getSafeNumber(r?.overallScore) ||
    getSafeNumber(metrics?.overallScore) ||
    getSafeNumber(performanceSummary?.overallScore) ||
    0;

  // Monatlicher Verlust: beste verfügbare Quelle
  const efficiencyGapMonthly = getSafeNumber(costInefficiency?.revenueEfficiencyGapMonthly);
  const marginalLift = getSafeNumber(metrics?.marginalLiftMonthlyGap);
  const remainingMonthly = getSafeNumber(goalPath?.remainingRevenueNeeded) / 12;

  // Auch: monthlyRevenueGap aus computedMetrics direkt
  const monthlyRevenueGap = getSafeNumber(metrics?.monthlyRevenueGap);

  const monthlyLoss =
    efficiencyGapMonthly ||
    monthlyRevenueGap ||
    marginalLift ||
    remainingMonthly ||
    0;

  // Jährlicher Verlust
  const efficiencyGapAnnual = getSafeNumber(costInefficiency?.revenueEfficiencyGapAnnual);
  const annualRevenueGap = getSafeNumber(metrics?.annualRevenueGap);
  const remaining = getSafeNumber(goalPath?.remainingRevenueNeeded);

  const annualLoss =
    efficiencyGapAnnual ||
    annualRevenueGap ||
    remaining ||
    monthlyLoss * 12;

  // Blockierende Funnel-Stufen
  const stages = funnelDiagnosis?.stages;
  const blockingStages = Array.isArray(stages)
    ? stages.filter((stage: unknown) => {
        const s = stage as Record<string, unknown>;
        return !!s?.isBlocking;
      }).length
    : 0;

  // Closing Rate (optional)
  const closingRate =
    getSafeNumber(metrics?.closingRate) ||
    getSafeNumber(r?.closingRate) ||
    undefined;

  // Maturity Gap
  const maturityGap = clamp(100 - overallScore, 0, 100);

  return {
    overallScore,
    monthlyLoss,
    annualLoss,
    blockingStages,
    closingRate,
    maturityGap,
  };
}

export function getPackageRecommendation(result: AuditResultLike): PackageRecommendation {
  const {
    overallScore,
    monthlyLoss,
    annualLoss,
    blockingStages,
    closingRate,
    maturityGap,
  } = extractCommercialSignals(result);

  const urgency: "niedrig" | "mittel" | "hoch" =
    annualLoss >= 180000 || blockingStages >= 3 || overallScore < 50
      ? "hoch"
      : annualLoss >= 60000 || blockingStages >= 2 || overallScore < 70
      ? "mittel"
      : "niedrig";

  // ── Paket 3: Transformation ────────────────────────────────────────────────
  if (
    annualLoss >= 240000 ||
    monthlyLoss >= 20000 ||
    blockingStages >= 3 ||
    overallScore < 45
  ) {
    return {
      key: "transformation",
      name: "Paket 3 · Transformation",
      shortLabel: "Transformation",
      priceLabel: "7.500–14.000 €",
      durationLabel: "12–20 Wochen",
      ctaLabel: "Transformation besprechen",
      urgency,
      headline:
        "Ihr Audit zeigt keine einzelne Baustelle, sondern ein strukturelles Wachstumsproblem.",
      rationale:
        "Bei diesem Muster reichen einzelne Quick Wins meist nicht aus. Die Daten sprechen für einen systematischen Umbau von Prozess, Conversion und Steuerung.",
      outcome: [
        "Vertriebsprozess neu strukturieren",
        "Kritische Funnel-Lecks systematisch schließen",
        "Conversion- und Pipeline-Steuerung aufbauen",
        "Klare Basis für nachhaltiges Wachstum schaffen",
      ],
      reasons: [
        `Messbarer Umsatzverlust von ${formatCurrency(monthlyLoss)} pro Monat`,
        `${blockingStages} blockierende Funnel-Stufen identifiziert`,
        `Niedriger Gesamt-Reifegrad von ${overallScore.toFixed(0)} / 100`,
        "Starker Hebel liegt eher im System als in Einzelmaßnahmen",
      ],
      nextStep:
        "Strategischer Umbau mit priorisiertem Maßnahmenplan, Führungsschleifen und Umsetzungslogik.",
      monthlyLoss,
      annualLoss,
      scoreSignals: {
        overallScore,
        blockingStages,
        monthlyLoss,
        annualLoss,
        closingRate,
        maturityGap,
      },
    };
  }

  // ── Paket 2: Optimierung ──────────────────────────────────────────────────
  if (
    annualLoss >= 90000 ||
    monthlyLoss >= 8000 ||
    blockingStages >= 2 ||
    overallScore < 70
  ) {
    return {
      key: "optimierung",
      name: "Paket 2 · Optimierung",
      shortLabel: "Optimierung",
      priceLabel: "3.500–6.000 €",
      durationLabel: "6–10 Wochen",
      ctaLabel: "Optimierung starten",
      urgency,
      headline:
        "Ihr größter Hebel liegt in der gezielten Optimierung bestehender Vertriebsleistung.",
      rationale:
        "Das Fundament ist vorhanden, aber die aktuelle Struktur verschenkt spürbar Umsatz. Hier ist der schnellste Hebel, die größten Verlusttreiber priorisiert zu beheben.",
      outcome: [
        "Größte Umsatzlecks zuerst schließen",
        "Bestehenden Funnel effizienter machen",
        "Closing- und Prozessqualität verbessern",
        "Schneller messbare Resultate erzeugen",
      ],
      reasons: [
        `Aktueller Umsatzverlust von ${formatCurrency(monthlyLoss)} pro Monat`,
        `${blockingStages} blockierende Funnel-Stufen`,
        `Reifegrad aktuell bei ${overallScore.toFixed(0)} / 100`,
        "Optimierung verspricht den besten Return ohne kompletten Umbau",
      ],
      nextStep:
        "Gezielte Hebelarbeit an Conversion, Prioritäten, Prozessqualität und Umsetzungsdisziplin.",
      monthlyLoss,
      annualLoss,
      scoreSignals: {
        overallScore,
        blockingStages,
        monthlyLoss,
        annualLoss,
        closingRate,
        maturityGap,
      },
    };
  }

  // ── Paket 1: Diagnose ─────────────────────────────────────────────────────
  return {
    key: "diagnose",
    name: "Paket 1 · Diagnose",
    shortLabel: "Diagnose",
    priceLabel: "1.200–2.500 €",
    durationLabel: "1–2 Wochen",
    ctaLabel: "Diagnose vertiefen",
    urgency,
    headline:
      "Ihr Audit zeigt relevante Hebel, aber der nächste Schritt ist vor allem Priorisierung und Schärfung.",
    rationale:
      "Der Umsatzverlust ist sichtbar, aber noch nicht so strukturell, dass sofort ein größerer Umbau nötig ist. Hier schafft eine saubere Diagnose mit Priorisierung den höchsten Nutzen.",
    outcome: [
      "Größte Hebel sauber sortieren",
      "Quick Wins von strukturellen Themen trennen",
      "30–90-Tage-Fahrplan schaffen",
      "Klare Grundlage für Paket 2 oder 3 schaffen",
    ],
    reasons: [
      `Sichtbarer Umsatzverlust von ${formatCurrency(monthlyLoss)} pro Monat`,
      `Aktuell ${blockingStages} kritische Funnel-Engpässe`,
      `Reifegrad bei ${overallScore.toFixed(0)} / 100`,
      "Zuerst Klarheit und Reihenfolge, dann Umsetzung",
    ],
    nextStep:
      "Vertiefte Diagnose mit sauberer Priorisierung, wirtschaftlicher Einordnung und konkretem Maßnahmenfahrplan.",
    monthlyLoss,
    annualLoss,
    scoreSignals: {
      overallScore,
      blockingStages,
      monthlyLoss,
      annualLoss,
      closingRate,
      maturityGap,
    },
  };
}

export function getRetainerRecommendation(result: AuditResultLike) {
  const { monthlyLoss, annualLoss, overallScore } = extractCommercialSignals(result);

  const recommended =
    annualLoss >= 180000 || overallScore < 60
      ? "3.000–4.500 €/Monat"
      : "1.800–3.000 €/Monat";

  return {
    name: "Revenue Retainer",
    priceLabel: recommended,
    rationale:
      "Sinnvoll nach Paket 2 oder 3, wenn Momentum gehalten, Ergebnisse stabilisiert und weitere Hebel systematisch ausgerollt werden sollen.",
    monthlyLoss,
    annualLoss,
    overallScore,
  };
}
